import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Banner, BannerActions, BannerDescription, BannerTitle,
} from '@bingads-webui/inline-copilot-ui';
import { Toaster, ToastOffset } from '@fluentui/react-toast';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { mergeClasses } from '@fluentui/react-components';
import { merge } from '@bingads-webui-universal/primitive-utilities';

import { useBannerService } from './banner-service';
import { useCopilotRepositoryContext } from '../CopilotRepositoryProvider';
import { getBannerErrorMsgs, getBannerStatesConfig } from './utils';
import { useCopilotBannerToast } from './use-copilot-banner-toast';
import { getCopilotRecommendationBannerStyles } from './CopilotRecommendationBanner.styles';
import { AssetsRecommendationData } from '../../types';

const DEFAULT_TOAST_OFFSET: ToastOffset = { horizontal: 20, vertical: 120 };

export interface ICopilotRecommendationBannerProps {
  className?: string;
  isToastEnabled?: boolean;
  onBannerRefreshCompleted?: () => void;
  onRefresh: () => void;
  onReset: () => void;
  toastOffset?: ToastOffset;
  shouldFetchRecommendations?: boolean;
  onFetch?: (finalUrl: string) => void;
  onFetchFailed?: (error: Error) => void;
  onFetchSucceeded?: (finalUrl: string, assetsResponse: AssetsRecommendationData) => void;
  narrow?: boolean;
  customStrings?: Record<string, any>;
}

export const CopilotRecommendationBanner: React.FC<ICopilotRecommendationBannerProps> = React.memo((props) => {
  const {
    className,
    isToastEnabled = true,
    toastOffset = DEFAULT_TOAST_OFFSET,
    onRefresh,
    onReset,
    onBannerRefreshCompleted,
    shouldFetchRecommendations = false,
    onFetch,
    onFetchFailed,
    onFetchSucceeded,
    narrow,
    customStrings = {},
  } = props;

  const i18n = useContext(I18nContext) as I18nModel;
  const repository = useCopilotRepositoryContext();
  const classes = getCopilotRecommendationBannerStyles();

  const { send, state } = useBannerService();

  const defaultBannerStates = useMemo(() => getBannerStatesConfig(i18n), [i18n]);
  const bannerStates = merge(defaultBannerStates, customStrings);
  const currentState = useMemo(() => bannerStates[state.value], [bannerStates, state.value]);

  const [isCustomerIdInExclusionList, setIsCustomerIdInExclusionList] = useState<boolean | undefined>();
  const [title, setTitle] = useState<string>();
  const [description, setDescription] = useState<React.ReactNode>();

  const {
    cancelFetchThemesJob,
    fetchAssets,
    finalUrl,
    getIsCustomerIdInExclusionList,
    perfMarker,
  } = repository || {};

  useEffect(() => {
    // fetch if the current customer is excluded from prefilling recommendations
    getIsCustomerIdInExclusionList?.().then((isExcluded) => {
      setIsCustomerIdInExclusionList(isExcluded);
    });
  }, [getIsCustomerIdInExclusionList]);

  useEffect(() => {
    if (shouldFetchRecommendations && finalUrl && fetchAssets && isCustomerIdInExclusionList != null) {
      const fetchPerfMarker = perfMarker?.createChild('fetchThemeAssetsForBanner');
      fetchPerfMarker?.willInit();

      cancelFetchThemesJob?.();

      send('Generate');
      onFetch?.(finalUrl);
      fetchPerfMarker?.willFetchData();
      fetchAssets().then((assetsResponse) => {
        send('GenerateSucceeded');
        fetchPerfMarker?.willRender();
        onFetchSucceeded?.(finalUrl, { ...assetsResponse, IsCustomerIdInExclusionList: isCustomerIdInExclusionList });
      }).catch((error) => {
        send('GenerateFailed', { error: error.message });
        onFetchFailed?.(error);
      }).finally(() => {
        fetchPerfMarker?.done();
      });
    }
  }, [
    cancelFetchThemesJob,
    fetchAssets,
    finalUrl,
    isCustomerIdInExclusionList,
    perfMarker,
    send,
    shouldFetchRecommendations,
  ]);

  useEffect(() => {
    if ((state.value === 'ApplyingFailed' || state.value === 'GeneratingFailed')
      && state.context.error) {
      const bannerErrorMsgs = getBannerErrorMsgs(i18n);
      const bannerErrorMsg = bannerErrorMsgs[state.context.error] || bannerErrorMsgs.NoRecommendation;

      setTitle(bannerErrorMsg.title);
      setDescription(bannerErrorMsg.description);
    } else {
      setTitle(currentState.title);
      setDescription(currentState.description);
    }
  }, [i18n, currentState.description, currentState.title, state.context.error, state.value]);

  const { actions = [] } = currentState;
  const { toasterId } = useCopilotBannerToast({ i18n, isToastEnabled, currentState: state.value });

  const isBannerRefreshCompleted = state.value === 'GeneratingSucceeded' || state.value === 'ApplyingSucceeded';
  useEffect(() => {
    if (isBannerRefreshCompleted) {
      onBannerRefreshCompleted?.();
    }
  }, [isBannerRefreshCompleted, onBannerRefreshCompleted]);

  if (state.value === 'Init') {
    // don't render banner until final URL is entered and recommendations start generating
    return null;
  }

  return (
    <>
      <Banner
        className={mergeClasses('copilot-banner-container', className)}
        locale={i18n.getLocale()}
        narrow={narrow}
      >
        <BannerTitle>{title}</BannerTitle>
        {description && (
          <BannerDescription>{description}</BannerDescription>
        )}
        <BannerActions
          actions={actions}
          onRefresh={onRefresh}
          onReset={onReset}
        />
      </Banner>
      {isToastEnabled && (
        <Toaster
          className={classes.toaster}
          toasterId={toasterId}
          offset={toastOffset}
        />
      )}
    </>
  );
});
